/**
 * What the shared access sheet DOES (2026-08-16, PLAN-connected-apps-governance-20260816 §6), apart from how it looks
 * (PV14 part 2a, 2026-10-09): which preset is chosen, whether the stored profile is "custom", whether anything may be
 * pressed, the fine-tune section (collapsed, but opened by itself on a custom profile so the state is always
 * explainable), and the profile a class's box toggles to (read never toggles: it is the floor of being connected).
 * ./AppAccessSheet.tsx draws it on every surface that shows the sheet; the new design's onboarding draws the same hook.
 */
import { useEffect, useState } from 'react';
import {
  profileFromTier,
  type AccessTier,
  type AccessTierInfo,
  type AccessProfile,
  type CapabilityClass,
} from './ComposioClient';

/** Render order is fixed: least to most dangerous (§3.1, inverted). */
export const CLASS_ORDER: CapabilityClass[] = ['read', 'create', 'send', 'delete', 'admin'];

/** The three presets, in the order the sheet lists them. */
export const ACCESS_PRESETS: AccessTier[] = ['full', 'read-write', 'read-only'];

/** The preset a profile matches, or null when it matches none ("Custom"). */
export function presetForProfile(profile: AccessProfile): AccessTier | null {
  const tiers: AccessTier[] = ['full', 'read-write', 'read-only'];
  for (const tier of tiers) {
    const p = profileFromTier(tier);
    if (
      p.create === profile.create &&
      p.send === profile.send &&
      p.delete === profile.delete &&
      p.admin === profile.admin
    ) {
      return tier;
    }
  }
  return null;
}

export interface AppAccessSheetState {
  /** The checked state: the stored profile, else the tier's lossless preset. */
  profile: AccessProfile;
  isCustom: boolean;
  /** The preset drawn as chosen (none while the profile is custom or still loading). */
  selectedTier: AccessTier | null;
  /** Anything may be pressed: the access has arrived, the person may manage it, and nothing is saving. */
  interactive: boolean;
  fineTuneOpen: boolean;
  toggleFineTune: () => void;
  /** A class's box pressed: the profile with that class flipped, handed to onSaveProfile. Read never toggles. */
  toggleClass: (cls: CapabilityClass) => void;
}

export function useAppAccessSheet({ access, canManage, saving, onSaveProfile }: {
  access: AccessTierInfo | null;
  canManage: boolean;
  saving: boolean;
  onSaveProfile: (profile: AccessProfile) => void;
}): AppAccessSheetState {
  const [fineTuneOpen, setFineTuneOpen] = useState(false);

  // Auto-expand on a custom profile — a "Custom" chip with the class rows
  // hidden would be unexplainable.
  useEffect(() => {
    if (access?.custom) setFineTuneOpen(true);
  }, [access?.custom]);

  // Current checked state: stored profile, else the tier's lossless preset.
  const profile: AccessProfile = access
    ? access.profile ?? profileFromTier(access.tier)
    : profileFromTier('full');
  const isCustom = access ? (access.custom ?? presetForProfile(profile) === null) : false;
  const selectedTier: AccessTier | null = access && !isCustom ? access.tier : null;
  const interactive = !!access && canManage && !saving;

  const toggleClass = (cls: CapabilityClass) => {
    // Read never toggles — it is the floor of being connected.
    if (cls === 'read' || !interactive) return;
    onSaveProfile({
      read: true,
      create: cls === 'create' ? !profile.create : profile.create,
      send: cls === 'send' ? !profile.send : profile.send,
      delete: cls === 'delete' ? !profile.delete : profile.delete,
      admin: cls === 'admin' ? !profile.admin : profile.admin,
    });
  };

  return { profile, isCustom, selectedTier, interactive, fineTuneOpen, toggleFineTune: () => setFineTuneOpen((open) => !open), toggleClass };
}
